
export interface IGPSCoordinate {
    lat: number;
    lon: number;
}

export interface IGPSLibrary {
    PI: number;
    x_pi: number;
    delta: (lat: number, lon: number) => { lat: number; lon: number; };
    gcj_encrypt: (wgsLat: number, wgsLon: number) => IGPSCoordinate;
    gcj_decrypt: (gcjLat: number, gcjLon: number) => IGPSCoordinate;
    gcj_decrypt_exact: (gcjLat: number, gcjLon: number) => IGPSCoordinate;
    bd_encrypt: (gcjLat: number, gcjLon: number) => IGPSCoordinate;
    bd_decrypt: (bdLat: number, bdLon: number) => IGPSCoordinate;
    mercator_encrypt: (wgsLat: number, wgsLon: number) => IGPSCoordinate;
    mercator_decrypt: (mercatorLat: number, mercatorLon: number) => IGPSCoordinate;
    distance: (latA: number, lonA: number, latB: number, lonB: number) => number;
    outOfChina: (lat: number, lon: number) => boolean;
    transformLat: (x: number, y: number) => number;
    transformLon: (x: number, y: number) => number;
}

const GPS: IGPSLibrary = {
    PI: 3.14159265358979324,
    x_pi: 3.14159265358979324 * 3000.0 / 180.0,
    delta: function (lat: number, lon: number): { lat: number; lon: number; } {
        // Krasovsky 1940
        //  a: 卫星椭球坐标投影到平面地图坐标系的投影因子。
        const a = 6378245.0;
        const ee = 0.00669342162296594323; //  ee: 椭球的偏心率。
        let dLat = this.transformLat(lon - 105.0, lat - 35.0);
        let dLon = this.transformLon(lon - 105.0, lat - 35.0);
        let radLat = lat / 180.0 * this.PI;
        let magic = Math.sin(radLat);
        magic = 1 - ee * magic * magic;
        const sqrtMagic = Math.sqrt(magic);
        dLat = (dLat * 180.0) / ((a * (1 - ee)) / (magic * sqrtMagic) * this.PI);
        dLon = (dLon * 180.0) / (a / sqrtMagic * Math.cos(radLat) * this.PI);
        return {'lat': dLat, 'lon': dLon};
    },
    gcj_encrypt: function (wgsLat: number, wgsLon: number): IGPSCoordinate {
        if (this.outOfChina(wgsLat, wgsLon))
            return {'lat': wgsLat, 'lon': wgsLon};

        const d = this.delta(wgsLat, wgsLon);
        return {'lat' : wgsLat + d.lat,'lon' : wgsLon + d.lon};
    },
    gcj_decrypt: function (gcjLat: number, gcjLon: number): IGPSCoordinate {
        if (this.outOfChina(gcjLat, gcjLon))
            return {'lat': gcjLat, 'lon': gcjLon};

        const d = this.delta(gcjLat, gcjLon);
        return {'lat': gcjLat - d.lat, 'lon': gcjLon - d.lon};
    },
    gcj_decrypt_exact: function (gcjLat: number, gcjLon: number): IGPSCoordinate {
        const initDelta = 0.01;
        const threshold = 0.000000001;
        let dLat = initDelta, dLon = initDelta;
        let mLat = gcjLat - dLat, mLon = gcjLon - dLon;
        let pLat = gcjLat + dLat, pLon = gcjLon + dLon;
        let wgsLat:number= 0;
        let wgsLon:number= 0;
        let i:number = 0;
        while (1) {
            wgsLat = (mLat + pLat) / 2;
            wgsLon = (mLon + pLon) / 2;
            const tmp = this.gcj_encrypt(wgsLat, wgsLon);
            dLat = tmp.lat - gcjLat;
            dLon = tmp.lon - gcjLon;
            if ((Math.abs(dLat) < threshold) && (Math.abs(dLon) < threshold))
                break;

            if (dLat > 0) pLat = wgsLat; else mLat = wgsLat;
            if (dLon > 0) pLon = wgsLon; else mLon = wgsLon;

            if (++i > 10000) break;
        }
        //console.log(i);
        return {'lat': wgsLat, 'lon': wgsLon};
    },
    bd_encrypt: function (gcjLat: number, gcjLon: number): IGPSCoordinate {
        const x = gcjLon, y = gcjLat;
        const z = Math.sqrt(x * x + y * y) + 0.00002 * Math.sin(y * this.x_pi);
        const theta = Math.atan2(y, x) + 0.000003 * Math.cos(x * this.x_pi);
        const bdLon = z * Math.cos(theta) + 0.0065;
        const bdLat = z * Math.sin(theta) + 0.006;
        return {'lat' : bdLat,'lon' : bdLon};
    },
    bd_decrypt: function (bdLat: number, bdLon: number): IGPSCoordinate {
        const x = bdLon - 0.0065, y = bdLat - 0.006;
        const z = Math.sqrt(x * x + y * y) - 0.00002 * Math.sin(y * this.x_pi);
        const theta = Math.atan2(y, x) - 0.000003 * Math.cos(x * this.x_pi);
        const gcjLon = z * Math.cos(theta);
        const gcjLat = z * Math.sin(theta);
        return {'lat' : gcjLat, 'lon' : gcjLon};
    },
    mercator_encrypt: function (wgsLat: number, wgsLon: number): IGPSCoordinate {
        const x = wgsLon * 20037508.34 / 180.;
        let y = Math.log(Math.tan((90. + wgsLat) * this.PI / 360.)) / (this.PI / 180.);
        y = y * 20037508.34 / 180.;
        return {'lat' : y, 'lon' : x};
    },
    mercator_decrypt: function (mercatorLat: number, mercatorLon: number): IGPSCoordinate {
        const x = mercatorLon / 20037508.34 * 180.;
        let y = mercatorLat / 20037508.34 * 180.;
        y = 180 / this.PI * (2 * Math.atan(Math.exp(y * this.PI / 180.)) - this.PI / 2);
        return {'lat' : y, 'lon' : x};
    },
    distance: function (latA: number, lonA: number, latB: number, lonB: number): number {
        const earthR = 6371000.;
        const x = Math.cos(latA * this.PI / 180.) * Math.cos(latB * this.PI / 180.) * Math.cos((lonA - lonB) * this.PI / 180);
        const y = Math.sin(latA * this.PI / 180.) * Math.sin(latB * this.PI / 180.);
        let s = x + y;
        if (s > 1) s = 1;
        if (s < -1) s = -1;
        let alpha = Math.acos(s);
        return alpha * earthR;
    },
    outOfChina: function (lat: number, lon: number): boolean {
        if (lon < 72.004 || lon > 137.8347)
            return true;
        if (lat < 0.8293 || lat > 55.8271)
            return true;
        return false;
    },
    transformLat: function (x: number, y: number): number {
        let ret = -100.0 + 2.0 * x + 3.0 * y + 0.2 * y * y + 0.1 * x * y + 0.2 * Math.sqrt(Math.abs(x));
        ret += (20.0 * Math.sin(6.0 * x * this.PI) + 20.0 * Math.sin(2.0 * x * this.PI)) * 2.0 / 3.0;
        ret += (20.0 * Math.sin(y * this.PI) + 40.0 * Math.sin(y / 3.0 * this.PI)) * 2.0 / 3.0;
        ret += (160.0 * Math.sin(y / 12.0 * this.PI) + 320 * Math.sin(y * this.PI / 30.0)) * 2.0 / 3.0;
        return ret;
    },
    transformLon: function (x: number, y: number): number {
        let ret = 300.0 + x + 2.0 * y + 0.1 * x * x + 0.1 * x * y + 0.1 * Math.sqrt(Math.abs(x));
        ret += (20.0 * Math.sin(6.0 * x * this.PI) + 20.0 * Math.sin(2.0 * x * this.PI)) * 2.0 / 3.0;
        ret += (20.0 * Math.sin(x * this.PI) + 40.0 * Math.sin(x / 3.0 * this.PI)) * 2.0 / 3.0;
        ret += (150.0 * Math.sin(x / 12.0 * this.PI) + 300.0 * Math.sin(x / 30.0 * this.PI)) * 2.0 / 3.0;
        return ret;
    }
};
export default GPS;
